/* The Atelier: product page buy box. <at-buybox> is the buy column of sections/main-product.liquid (loaded defer).
   - Sizes start unchosen: product-variant-picker renders no checked size pill and "Choose a size" until a size is
     picked or the URL pins a variant (Dawn preselects the first available variant, a known cause of wrong-size orders
     on kids' wear). Add to bag then asks for a size: the header reads "Choose her size first." in rose and a
     pre-rendered role=alert span announces it, so nothing is inserted and the row keeps its height. The phone bar's
     "Choose a size" only brings the sizes into view (she did what it said, so no error), as in the approved design.
   - The phone buy bar (snippets/at-buybar.liquid, a sibling inside <product-info>) mirrors the price, the Afterpay
     line and the button state: "Choose a size", "Add to bag" (a spinner while adding), disabled "Sold out", or "Notify me" while Klaviyo's
     back-in-stock trigger shows (product-info.at-bis-active, set by assets/at-klaviyo-bis.js with an at:bis event).
   - Desktop (at-buy--sticky, section setting "Sticky content"): the gallery and the buy box follow the page. A column
     that fits the viewport holds under the header; a taller one scrolls with the page until its end is in view and
     holds there, and on the way back up scrolls with the page until its top is in view and holds there. The shorter
     column moves, the taller one sets the height of the row. See "Desktop columns" below.
   - <at-hero-gallery> (hero gallery layouts only): swipe, ArrowLeft/ArrowRight and the "n / N" counter.
   - The chosen size's row is tinted in the size charts (markChart).
   - The reviews section's "Read all N reviews" control (a delegated click; see the end of this file).
   - The size guide popup's page body, fetched when the popup opens, focus on its Close when it opens, and the Tab loop
     in it and the lightbox (delegated; end of this file).
   - <at-pairs>: the "Pairs well with" block (Rebuy's picks, rendered without Rebuy's script; end of this file).
   Strings come from data-* attributes rendered in Liquid. Listeners are added in connectedCallback and removed in
   disconnectedCallback, so a theme-editor re-render never doubles them. No timers; one ResizeObserver (desktop
   columns), one on the buy bar's height, and an IntersectionObserver placing the bar where view timelines are missing. */
if (!customElements.get('at-buybox')) {
  customElements.define(
    'at-buybox',
    class AtBuybox extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.root = this.closest('product-info') || document;
        this.box = this.querySelector('.at-buy');
        // The product's own form: a "Pairs well with" row is a product-form too and can come first in the buy box.
        const mainForm = this.querySelector('#product-form-' + this.sectionId);
        this.productForm = (mainForm && mainForm.closest('product-form')) || this.querySelector('product-form:not(.at-pair__form)');
        this.bar = this.root.querySelector('[data-at-buybar]');
        this.barBtn = this.bar && this.bar.querySelector('[data-at-buybar-btn]');
        this.barLabel = this.barBtn && (this.barBtn.querySelector('[data-at-buybar-label]') || this.barBtn);
        this.barPrice = this.bar && this.bar.querySelector('[data-at-buybar-price]');
        this.barAfterpay = this.bar && this.bar.querySelector('[data-at-buybar-afterpay]');
        this.barOos = this.bar && this.bar.querySelector('[data-at-buybar-oos]');

        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onBis = this.onBis.bind(this);
        this.onBarClick = this.onBarClick.bind(this);
        this.onOosClick = this.onOosClick.bind(this);
        this.onBusy = this.onBusy.bind(this);
        this.onSubmitState = this.onSubmitState.bind(this);
        this.onPillClick = this.onPillClick.bind(this);

        // Capture on the custom element runs before Dawn's submit handler on the inner form.
        if (this.productForm) this.productForm.addEventListener('submit', this.onSubmit, true);
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onPillClick);
        this.addEventListener('at:add-busy', this.onBusy);
        this.addEventListener('at:submit-state', this.onSubmitState);
        if (this.box && this.box.classList.contains('at-buy--sticky')) this.startColumns();
        this.root.addEventListener('at:bis', this.onBis);
        if (this.barBtn) this.barBtn.addEventListener('click', this.onBarClick);
        if (this.barOos) this.barOos.addEventListener('click', this.onOosClick);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
            this.pending = null;
            this.syncOptionCopy();
            this.syncBar();
            this.markSoldOut();
          });
        }

        this.syncOptionCopy();
        this.syncBar();
        this.markSoldOut();
        this.markChart();
        this.watchBarHeight();
        this.watchBarPlace();

        // Add to bag pressed before this script ran (buy-buttons.liquid's inline onsubmit held it): add now, or once
        // the size picked before it (reconciled just below) has its variant id.
        const form = this.productForm && this.productForm.form;
        const early = form && form.dataset.atQueued;
        if (form) delete form.dataset.atQueued;
        this.reconcile();
        if (early) this.pending ? this.queueAdd() : this.flushAdd(true);
      }

      disconnectedCallback() {
        if (this.productForm) this.productForm.removeEventListener('submit', this.onSubmit, true);
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onPillClick);
        this.removeEventListener('at:add-busy', this.onBusy);
        this.removeEventListener('at:submit-state', this.onSubmitState);
        this.stopColumns();
        if (this.barObserver) this.barObserver.disconnect();
        if (this.placeObserver) this.placeObserver.disconnect();
        this.root.removeEventListener('at:bis', this.onBis);
        if (this.barBtn) this.barBtn.removeEventListener('click', this.onBarClick);
        if (this.barOos) this.barOos.removeEventListener('click', this.onOosClick);
        if (this.unsubscribe) this.unsubscribe();
      }

      // Accessories can have a legacy "Size" option for lengths or styles. The product's option name is kept when
      // it is specific; main-product supplies neutral copy for that legacy name, without changing catalog data.
      syncOptionCopy() {
        if (!this.dataset.choiceLabel) return;
        const sizes = this.sizes();
        if (sizes) {
          const label = sizes.querySelector('[data-at-size-label]');
          const legend = sizes.querySelector('legend');
          const picked = sizes.querySelector('input:checked');
          if (legend) legend.textContent = this.dataset.choicePrefix;
          if (label) {
            label.dataset.prefix = this.dataset.choicePrefix + ' ';
            if (!label.classList.contains('is-error')) label.textContent = picked ? label.dataset.prefix + picked.value : this.dataset.choiceLabel;
          }
        }
        if (this.barBtn) this.barBtn.dataset.labelChoose = this.dataset.choiceLabel;
      }

      // Larger text can wrap the buy bar onto two rows. Its scroll ranges and floating pills need that real height,
      // while CSS keeps only the first-paint minimum, so reducing the text size can shrink it again.
      watchBarHeight() {
        if (!this.bar) return;
        this.barObserver = new ResizeObserver(() => {
          if (!this.bar.offsetHeight) return;
          document.body.style.setProperty('--at-buybar-measured', this.bar.offsetHeight + 'px');
        });
        this.barObserver.observe(this.bar);
      }

      // Browsers without scroll-driven animations (Firefox; Safari before 26): show the bar only once the in-page buy
      // form has gone up under the header, as the view timeline does elsewhere (at-product.css, body.at-buybar-on).
      watchBarPlace() {
        const form = this.querySelector('.at-buy-form');
        if (!this.bar || !form || CSS.supports('animation-timeline', 'view()')) return;
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--at-header-h')) || 0;
        this.placeObserver = new IntersectionObserver(([entry]) => {
          document.body.classList.toggle('at-buybar-on', !entry.isIntersecting && entry.boundingClientRect.top < header);
        }, { rootMargin: `-${Math.round(header)}px 0px 0px 0px` });
        this.placeObserver.observe(form);
      }

      sizes() {
        return this.querySelector('.at-sizes');
      }

      sizeChosen() {
        const sizes = this.sizes();
        return !sizes || !!sizes.querySelector('input[type=radio]:checked');
      }

      bisTrigger() {
        return this.root.classList.contains('at-bis-active') ? this.querySelector('.klaviyo-bis-trigger') : null;
      }

      // "Choose her size first." in the size header (rose) and in the live region; the row height never changes.
      showError() {
        const sizes = this.sizes();
        if (!sizes) return;
        const label = sizes.querySelector('[data-at-size-label]');
        const alert = sizes.querySelector('[data-at-size-alert]');
        if (label) {
          label.classList.add('is-error');
          label.textContent = this.dataset.chooseFirst;
        }
        if (alert) alert.textContent = this.dataset.chooseFirst;
      }

      askForSize() {
        this.showError();
        const sizes = this.sizes();
        if (!sizes) return;
        // Usually the sizes are on screen just above Add to bag: the header turns rose in place and the page stays
        // still (it used to slide 40-55px to centre them). Only sizes out of view, or under the header, are scrolled to.
        const rect = sizes.getBoundingClientRect();
        if (rect.top < this.pinTop() - 24 || rect.bottom > window.innerHeight) sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // A size that is checked but was never heard (tapped before the scripts ran, or put back by the browser on Back):
      // the form still holds the server's variant id, the first size in stock. Every render checks exactly the
      // selected variant's pills (the checked attribute), so a checked pill without it is one Dawn never fetched.
      stalePick() {
        const selects = this.querySelector('variant-selects');
        return selects ? Array.from(selects.querySelectorAll('input[type=radio]')).find((input) => input.checked && !input.defaultChecked) : null;
      }

      // Replays a stale pick as a change, so Dawn's picker fetches its variant (and the header, bar and chart follow).
      reconcile() {
        const input = !this.pending && this.stalePick();
        if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSubmit(event) {
        const stale = this.sizeChosen() && this.stalePick();
        if (this.sizeChosen() && !stale) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!stale) return this.askForSize();
        // A size just picked that is sold out (the pill says so; the button already reads "Sold out") adds nothing.
        if (this.pending && this.pending.classList.contains('disabled')) return;
        this.reconcile();
        this.queueAdd();
      }

      // An Add to bag that must wait for the chosen size's variant id (either button pressed while the size's
      // re-render is on its way, which product-info.js leaves enabled on this page, or Add to bag pressed before this
      // script ran): both buttons spin, and the add goes ahead when the in-page button settles (onSubmitState). If
      // the size turns out sold out or unavailable, nothing is added.
      queueAdd() {
        this.queued = true;
        this.spin(true);
      }

      flushAdd(now) {
        if (!now && !this.queued) return;
        this.queued = false;
        this.spin(false);
        const submit = this.querySelector('#ProductSubmitButton-' + this.sectionId);
        if (!submit || submit.disabled) return;
        // After Dawn's update has finished (its variantChange is published after the button settles).
        Promise.resolve().then(() => submit.click());
      }

      // The spinner both buttons show while adding (in-page: at-product.css .product-form__submit.loading).
      spin(on) {
        const submit = this.querySelector('#ProductSubmitButton-' + this.sectionId);
        [this.barBtn, submit].forEach((btn) => {
          if (!btn) return;
          btn.classList.toggle('loading', on);
          if (on) btn.setAttribute('aria-busy', 'true');
          else btn.removeAttribute('aria-busy');
          const spinner = btn.querySelector('.loading__spinner');
          if (spinner) spinner.classList.toggle('hidden', !on);
        });
      }

      // A size whose re-render failed twice leaves "Unavailable" (product-info.js). Tapping that size again is the
      // natural retry, but a checked radio fires no change, so the tap did nothing: it is turned into a change here.
      onPillClick(event) {
        const input = event.target;
        if (!this.failedPick || input !== this.failedPick || !input.checked) return;
        this.failedPick = null;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // A pick shows its final text at once ("Size 4"). product-info.js resolves the size from the page's data in the
      // same frame (applyVariantLocally: the form's id, the button, the price), and marks the checked pill as the page's
      // own (defaultChecked). A user tap reaches this handler after that (the microtask runs as soon as the picker's own
      // listener returns), so the pick is complete and nothing is pending. A replayed change (reconcile) reaches it
      // first: the pick is pending until the local update's at:submit-state (onSubmitState). Only a size missing from
      // the page's data waits for the section re-render, with the bar predicting Sold out from the pill meanwhile.
      onChange(event) {
        const input = event.target;
        if (!input.matches || !input.matches('.product-form__input--pill input[type=radio]')) return;
        const fieldset = input.closest('fieldset');
        const label = fieldset && fieldset.querySelector('[data-at-size-label]');
        if (label) {
          label.classList.remove('is-error');
          label.textContent = (label.dataset.prefix || '') + input.value;
        }
        const alert = fieldset && fieldset.querySelector('[data-at-size-alert]');
        if (alert) alert.textContent = '';
        this.pending = input.defaultChecked ? null : input;
        this.failedPick = null;
        this.syncBarButton(this.pending || undefined);
        this.markSoldOut(this.pending || undefined);
        this.markChart();
      }

      // A chosen size that is sold out: the submit's slot shows a quiet outlined "Sold out" (at-product.css
      // .is-size-sold-out), the placeholder Klaviyo's "Notify me" replaces in place, instead of a half-faded rose
      // button. Right after a pick (picked = the input) the pill's sold-out class decides and the label is written at
      // once (Dawn's re-render brings the same words); otherwise the real submit's disabled state does.
      markSoldOut(picked) {
        const form = this.querySelector('.at-buy-form');
        const submit = this.querySelector('#ProductSubmitButton-' + this.sectionId);
        if (!form || !submit) return;
        const soldOut = picked ? picked.classList.contains('disabled') : submit.hasAttribute('disabled');
        form.classList.toggle('is-size-sold-out', soldOut);
        const label = submit.querySelector('span');
        if (picked && soldOut && label && window.variantStrings) label.textContent = window.variantStrings.soldOut;
      }

      // The chosen size's row is tinted in the size charts (Size & fit and the size guide popup, which Dawn moves to
      // <body>), as in the approved design: a row whose first cell reads exactly a checked pill's value ("8"). Charts
      // are one row per size, so a chart that does not match (another layout, a one-size product) simply shows no tint.
      // Each row whose size is in stock also picks it (data-at-pick; the delegated handler is at the end of this file):
      // the whole row takes a tap or click, and its first cell's size becomes a real <button> named "Choose size 8" for
      // keyboards and screen readers, so the table keeps its row and cell semantics. Sold-out sizes keep their
      // measurements, with a crossed-out size and an explanation; rows with no pill stay plain text.
      markChart() {
        const pills = new Map(Array.from(this.querySelectorAll('.at-sizes input[type=radio]'), (input) => [input.value.trim(), input]));
        const picked = Array.from(this.querySelectorAll('.at-sizes input[type=radio]:checked'), (input) => input.value.trim());
        const name = this.dataset.chartPick || '[size]';
        document.querySelectorAll('.at-size-chart__table tr').forEach((row) => {
          const cell = row.cells[0];
          const value = row.dataset.atSize || (cell ? cell.textContent.trim() : '');
          const pill = value && pills.get(value);
          if (pill) row.dataset.atSize = value;
          const unavailable = !!pill && (pill.classList.contains('disabled') || pill.disabled);
          row.classList.toggle('is-unavailable', unavailable);
          row.classList.toggle('is-size', !!cell && picked.includes(value));
          if (pill && !unavailable) {
            row.dataset.atPick = value;
            if (!cell.querySelector('.at-chart-pick')) {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'at-chart-pick';
              button.textContent = value;
              cell.replaceChildren(button);
            }
            cell.querySelector('.at-chart-pick').setAttribute('aria-label', name.replace('[size]', value));
          } else if (unavailable) {
            delete row.dataset.atPick;
            if (!cell.querySelector('.at-chart-unavailable')) {
              const size = document.createElement('span');
              size.className = 'at-chart-unavailable';
              size.textContent = value;
              const explanation = document.createElement('span');
              explanation.className = 'visually-hidden';
              explanation.textContent = ' — ' + row.closest('.at-size-chart').dataset.unavailable;
              cell.replaceChildren(size, explanation);
            }
          } else if (row.dataset.atPick || row.dataset.atSize) {
            delete row.dataset.atPick;
            delete row.dataset.atSize;
            cell.textContent = value;
          }
        });
        document.querySelectorAll('.at-size-chart').forEach((chart) => {
          const cue = chart.querySelector('.at-size-chart__cue');
          if (cue) cue.hidden = !chart.querySelector('tr[data-at-pick]');
        });
      }

      // A size chart row was chosen: check that size's pill as a tap on it would (Dawn's change handler re-renders),
      // close the size guide if the row is in it, and put the shopper on the size row, focused on the size.
      pickFromChart(value) {
        const input = Array.from(this.querySelectorAll('.at-sizes input[type=radio]')).find((el) => el.value.trim() === value);
        if (!input || input.classList.contains('disabled') || input.disabled) return;
        const modal = document.querySelector('.product-popup-modal[open]');
        if (modal && typeof modal.hide === 'function') modal.hide();
        if (!input.checked) input.click();
        const sizes = this.sizes();
        if (sizes) {
          const rect = sizes.getBoundingClientRect();
          if (rect.top < this.pinTop() - 24 || rect.bottom > window.innerHeight) sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        input.focus({ preventScroll: true });
      }

      // ---- Desktop columns (at-buy--sticky only; CSS in at-product.css) ----
      // Each column's content (the gallery; the buy box) is position:sticky in its grid cell, below a spacer (the
      // cell's ::before, height --at-lead). JS acts only when the scroll direction changes, a column changes height, or
      // the window resizes, and it never moves anything: the spacer is set so the column's normal place is exactly
      // where it is on screen, and one sticky edge says where it will hold.
      //   Going down: top = the end-in-view line (viewport - height - 24; the pin line for a column that fits), or
      //   where it is now if that is higher up, so a tall column scrolls with the page until its end shows, then holds.
      //   Going up: bottom = the value that holds its top at the pin line (header + 24), or where it is now if lower,
      //   so it scrolls with the page until its top shows, then holds.
      // The taller column fills its cell, so it never moves; the shorter one follows. Between those moments the
      // browser does all the work (compositor-scrolled sticky). The scroll handler otherwise records where each column
      // is, so a height change (accordion, size pick, Pairs well with) keeps the column on the pixel it was on.
      startColumns() {
        this.grid = this.parentElement;
        const media = this.grid && this.grid.querySelector(':scope > .product__media-wrapper');
        this.cols = [{ cell: this, box: this.box }];
        if (media && media.firstElementChild) this.cols.push({ cell: media, box: media.firstElementChild });
        this.heading = 1;
        this.lastY = window.scrollY;
        this.onScroll = this.onScroll.bind(this);
        this.onResize = () => this.place(true);
        this.onMedia = this.onMedia.bind(this);
        this.observer = new ResizeObserver(() => this.place(false));
        this.desk = window.matchMedia('(min-width:801px)');
        this.desk.addEventListener('change', this.onMedia);
        this.onMedia();
      }

      stopColumns() {
        if (!this.desk) return;
        this.desk.removeEventListener('change', this.onMedia);
        this.follow(false);
        this.desk = null;
      }

      onMedia() {
        this.follow(this.desk.matches);
      }

      follow(on) {
        this.grid.classList.toggle('at-follow', on);
        if (on) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onResize, { passive: true });
          this.cols.forEach((col) => this.observer.observe(col.box)); // its first callback places the columns
          return;
        }
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.observer.disconnect();
        this.cols.forEach((col) => {
          col.cell.style.removeProperty('--at-lead');
          col.box.style.top = col.box.style.bottom = '';
          col.top = col.h = null;
        });
      }

      pinTop() {
        return (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--at-header-h')) || 0) + 24;
      }

      onScroll() {
        const y = window.scrollY;
        const dir = y > this.lastY ? 1 : y < this.lastY ? -1 : 0;
        this.lastY = y;
        if (dir && dir !== this.heading) {
          this.heading = dir;
          this.place(true);
        } else {
          this.cols.forEach((col) => (col.top = col.box.getBoundingClientRect().top));
        }
      }

      // fresh: read where each column is now (scroll, resize). Otherwise (a height change) use where it was at the last
      // scroll, before the change moved it. All reads first, then all writes.
      // A height change while heading down also puts the OTHER column where it belongs for this scroll position at
      // once. Shorter: its end on the end-in-view line (its top on the pin line if it fits), never past the changed
      // column's end; opening Size & fit at the foot of the section used to leave the gallery ending up to 680px above
      // that line (stopped at the old section end) until the next scroll up. Taller, with the changed column now ending
      // in view: it ends together with it (closing Size & fit again brings back the state before it opened). The column
      // that changed (the clicked summary's) always keeps its pixel.
      place(fresh) {
        const pin = this.pinTop();
        const vh = window.innerHeight;
        const read = this.cols.map((col) => ({
          col,
          top: fresh || col.top == null ? col.box.getBoundingClientRect().top : col.top,
          cell: col.cell.getBoundingClientRect().top,
          height: col.box.offsetHeight,
        }));
        const changed = !fresh && this.heading > 0 && read.some((r) => r.col.h != null && r.col.h !== r.height);
        if (changed) {
          read.forEach((r) => {
            if (r.col.h !== r.height) return;
            const others = read.filter((o) => o !== r);
            if (!others.length) return;
            const end = Math.max(...others.map((o) => o.top + o.height)); // the changed column does not move
            if (r.height < Math.max(...others.map((o) => o.height))) {
              // Shorter: down to its line, but never past the other column's end (the section end).
              r.top = Math.min(Math.max(r.top, Math.min(pin, vh - 24 - r.height)), end - r.height);
            } else if (end <= vh - 24) {
              // Taller, and the other column now ends in view: end together with it (as far as its cell allows),
              // instead of leaving the section end below the clicked column.
              r.top = Math.max(r.cell, Math.min(r.top, end - r.height));
            }
          });
        }
        read.forEach(({ col, top, cell, height }) => {
          col.top = top;
          col.h = height;
          col.cell.style.setProperty('--at-lead', Math.max(0, top - cell) + 'px');
          if (this.heading > 0) {
            col.box.style.top = Math.min(pin, vh - 24 - height, top) + 'px';
            col.box.style.bottom = '';
          } else {
            col.box.style.top = 'auto';
            col.box.style.bottom = vh - Math.max(pin, top) - height + 'px';
          }
        });
      }

      // For code that grows a column and then scrolls to what it revealed ("Show all N photos"): places the columns
      // now, as the ResizeObserver would before the next paint, so the scroll measures where things will be.
      settle() {
        if (this.desk && this.desk.matches) this.place(false);
      }

      currentPrice() {
        const root = this.querySelector('#price-' + this.sectionId);
        const el = root && (root.querySelector('.price--on-sale .price-item--sale') || root.querySelector('.price-item--regular'));
        return el ? el.textContent.trim() : null;
      }

      // The pending bag line for Add to bag (product-form.js showPending): the product's name, the size as the bag
      // writes it ("Size 8", from the size header; another option shows its value), the price showing, the bag line's
      // photo files for that size (snippets/at-variant-data.liquid) and the gallery's photo already on screen, which
      // paints at once. null (no pending line) if the size is not in the page's data.
      describeLine(id) {
        const info = this.closest('product-info');
        const data = info && info.atVariants;
        const variant = data && Array.isArray(data.variants) ? data.variants.find((v) => String(v.id) === String(id)) : null;
        if (!variant) return null;
        const title = this.querySelector('.at-buy-title h1');
        const size = this.querySelector('.at-sizes [data-at-size-label]');
        const groups = this.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown');
        const option = size && groups.length === 1 && !size.classList.contains('is-error') ? size.textContent.trim() : variant.title;
        const photo = info.querySelector('.product__media-list > li.is-active .product__media img, .product__media-list > li:first-child .product__media img');
        return {
          id: variant.id,
          title: title ? title.textContent.trim() : '',
          option,
          price: this.currentPrice(),
          img: variant.img,
          img2: variant.img2,
          shown: photo && photo.complete && photo.naturalWidth ? photo.currentSrc : null,
        };
      }

      // The Afterpay line inside #price- is re-rendered by Dawn on each size change; the bar copies its instalment.
      syncAfterpay() {
        if (!this.barAfterpay) return;
        const amount = this.querySelector('#price-' + this.sectionId + ' .at-afterpay b');
        this.barAfterpay.hidden = !amount;
        if (amount) this.barAfterpay.textContent = this.barAfterpay.dataset.format.replace('[amount]', amount.textContent.trim());
      }

      // The bar button mirrors the real submit: disabled "Sold out" while the chosen size is unavailable, "Notify me"
      // while Klaviyo's trigger shows, "Choose a size" until a size is picked, otherwise "Add to bag". Right after a
      // pick (picked = the input) the submit is still waiting for Dawn's re-render, so the pill's own sold-out class
      // (product-variant-options) decides.
      syncBarButton(picked) {
        if (!this.barBtn) return;
        const btn = this.barBtn;
        if (this.bisTrigger()) {
          btn.disabled = false;
          this.barLabel.textContent = btn.dataset.labelNotify;
          return;
        }
        const submit = this.querySelector('#ProductSubmitButton-' + this.sectionId);
        let soldOut;
        let soldOutLabel = btn.dataset.labelSoldOut;
        // Until Dawn's re-render lands the submit still shows the previous size's state; keep the prediction from the pill.
        picked = picked || this.pending;
        if (picked) {
          soldOut = picked.classList.contains('disabled');
        } else {
          soldOut = !!submit && submit.hasAttribute('disabled');
          if (soldOut) soldOutLabel = submit.textContent.trim() || soldOutLabel;
        }
        btn.disabled = soldOut;
        if (soldOut) this.barLabel.textContent = soldOutLabel;
        else this.barLabel.textContent = this.sizeChosen() ? btn.dataset.labelAdd : btn.dataset.labelChoose;
      }

      syncBar() {
        const price = this.currentPrice();
        if (this.barPrice && price) this.barPrice.textContent = price;
        this.syncAfterpay();
        this.syncBarButton();
      }

      // at:bis from assets/at-klaviyo-bis.js: detail.failed when back in stock cannot start (onsite.js blocked, failed, or
      // loaded without klaviyoBIS). A fully sold-out product's
      // bar link (at-buybar.liquid, data-bis) already reads "Notify me"; it only falls back to "Sold out · See similar".
      onBis(event) {
        this.syncBarButton();
        if (!this.barOos || !this.barOos.dataset.bis) return;
        if (this.bisTrigger()) this.barOos.dataset.bis = 'on';
        else if (event && event.detail && event.detail.failed) this.barOos.dataset.bis = 'off';
      }

      onBarClick() {
        const trigger = this.bisTrigger();
        if (trigger) return trigger.click();
        if (!this.sizeChosen()) return this.showSizes();
        // The chosen size's re-render has not landed: wait for it (onSubmit would queue a click on the real button too).
        if (this.pending || this.stalePick()) {
          this.reconcile();
          return this.queueAdd();
        }
        const submit = this.querySelector('#ProductSubmitButton-' + this.sectionId);
        if (submit) submit.click();
      }

      // The bar's "Choose a size": the sizes come into view with their calm "Choose a size" header, and focus moves to
      // the first size in stock so a screen reader or keyboard lands in the row (a tap shows no focus ring).
      showSizes() {
        const sizes = this.sizes();
        if (!sizes) return;
        sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const first = sizes.querySelector('input[type=radio]:not(.disabled):not(:disabled)') || sizes.querySelector('input[type=radio]');
        if (first) first.focus({ preventScroll: true });
      }

      // While the in-page form is adding (product-form.js at:add-busy), the bar's button shows the same white spinner
      // in place of its label: a tap on the bar clicks the in-page button, which is off screen. The Pairs well with
      // rows are product-forms inside this box too; only the main form counts.
      onBusy(event) {
        if (!this.barBtn || event.target !== this.productForm) return;
        const busy = !!(event.detail && event.detail.busy);
        this.barBtn.classList.toggle('loading', busy);
        if (busy) this.barBtn.setAttribute('aria-busy', 'true');
        else this.barBtn.removeAttribute('aria-busy');
        const spinner = this.barBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !busy);
        if (!busy) this.showAddError();
      }

      // An add that fails ("You can't add more … to the cart", or a dropped connection) writes its message above the
      // in-page Add to bag. Tapped from the phone bar, that is a screen or more away, so nothing seemed to happen: the
      // message now comes into view the way the bar's "Choose a size" brings the sizes. In view already (the buy box's
      // own button, the desktop column) nothing moves.
      showAddError() {
        const wrap = this.productForm && this.productForm.querySelector('.product-form__error-message-wrapper');
        if (!wrap || wrap.hidden) return;
        const rect = wrap.getBoundingClientRect();
        if (rect.top < this.pinTop() - 24 || rect.bottom > window.innerHeight - (this.bar ? this.bar.offsetHeight : 0)) {
          wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      // Dawn settled the in-page button (product-form.js at:submit-state). After a failed size re-render it reads
      // "Unavailable" with no variantChange, so the bar would keep its prediction ("Add to bag") and a tap would do
      // nothing: the bar now copies the button whenever it settles. Only the main form counts (Pairs well with rows).
      onSubmitState(event) {
        if (event.target !== this.productForm || !event.detail || !event.detail.final) return;
        this.pending = null;
        this.syncBarButton();
        this.markSoldOut();
        const label = this.querySelector('#ProductSubmitButton-' + this.sectionId + ' > span');
        const failed = !!label && !!window.variantStrings && label.textContent.trim() === window.variantStrings.unavailable;
        this.failedPick = failed ? this.stalePick() : null;
        this.flushAdd();
      }

      // Fully sold out: the bar is a "Sold out · See similar" link to the panel, or "Notify me" while Klaviyo's trigger shows.
      // A fully sold-out product's bar link: Klaviyo's form once its trigger shows; while it is still loading
      // (data-bis=pending) the in-page button where it will appear ("Sold out" until then); else the sold-out panel.
      onOosClick(event) {
        const trigger = this.bisTrigger();
        if (trigger) {
          event.preventDefault();
          trigger.click();
        } else if (this.barOos.dataset.bis === 'pending') {
          const buttons = this.querySelector('.product-form__buttons');
          if (!buttons) return;
          event.preventDefault();
          buttons.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  );
}

/* <at-hero-gallery> wraps the gallery on the hero layouts (gallery_layout hero / hero_mobile; main-product renders it
   only for them). Swipe on the photo (pointer events, 40px; the list has touch-action:pan-y in at-product.css), the
   previous/next arrows on the photo (desktop hero; shown on hover or keyboard focus, disabled at the first and last
   photo: no wrap, as SKIMS, Zimmermann and J.Crew do), ArrowLeft/ArrowRight, and the "n / N" counter. Every change
   clicks the thumbnail button (hidden ones too), so Dawn's media-gallery.js setActiveMedia keeps is-active,
   aria-current, the live region and variant photos in step. The counter and arrows are re-read after a thumbnail
   click (this element hears it bubble after Dawn's own handler) and after a size change (Dawn's variantChange is
   published once the variant photo is active). Hidden photos are loading="lazy"; the photos either side of the one
   showing are switched to eager when the shopper points at, touches or focuses the gallery and after every change,
   so the next photo is usually there before it is asked for, and nothing else loads up front. */
if (!customElements.get('at-hero-gallery')) {
  customElements.define(
    'at-hero-gallery',
    class AtHeroGallery extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.at-gallery.is-hero, .at-gallery.is-hero-mobile');
        this.thumbs = this.querySelector('.at-thumbs');
        if (!this.list || !this.thumbs) return;
        this.viewer = this.list.parentElement;
        this.counter = this.viewer.querySelector('.at-gallery-count');
        this.arrows = Array.from(this.viewer.querySelectorAll('[data-at-step]'));
        this.mq = window.matchMedia('(max-width:800px)');
        this.start = null;
        this.swiped = false;

        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);
        this.onCancel = () => { this.start = null; };
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onKey = this.onKey.bind(this);
        this.updateCounter = this.updateCounter.bind(this);
        this.onArrow = this.onArrow.bind(this);
        this.warm = this.warm.bind(this);

        this.viewer.addEventListener('pointerdown', this.onDown);
        this.viewer.addEventListener('pointerup', this.onUp);
        this.viewer.addEventListener('pointercancel', this.onCancel);
        // A finished swipe must not also open the lightbox (capture runs before Dawn's modal-opener click handler).
        this.viewer.addEventListener('click', this.onClickCapture, true);
        this.viewer.addEventListener('keydown', this.onKey);
        this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrow));
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) => this.addEventListener(type, this.warm, { passive: true }));
        this.thumbs.addEventListener('click', this.updateCounter);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.updateCounter);
        }
        this.updateCounter();
      }

      disconnectedCallback() {
        if (!this.viewer) return;
        this.viewer.removeEventListener('pointerdown', this.onDown);
        this.viewer.removeEventListener('pointerup', this.onUp);
        this.viewer.removeEventListener('pointercancel', this.onCancel);
        this.viewer.removeEventListener('click', this.onClickCapture, true);
        this.viewer.removeEventListener('keydown', this.onKey);
        this.arrows.forEach((arrow) => arrow.removeEventListener('click', this.onArrow));
        ['pointerenter', 'touchstart', 'focusin'].forEach((type) => this.removeEventListener(type, this.warm));
        this.thumbs.removeEventListener('click', this.updateCounter);
        if (this.unsubscribe) this.unsubscribe();
      }

      heroShowing() {
        return this.list.classList.contains('is-hero') || this.mq.matches;
      }

      slides() {
        return Array.from(this.list.querySelectorAll('li[data-media-id]'));
      }

      neighbour(dir) {
        const slides = this.slides();
        const i = slides.findIndex((li) => li.classList.contains('is-active'));
        return i < 0 ? null : slides[i + dir] || null;
      }

      step(dir) {
        const next = this.neighbour(dir);
        const button = next && this.thumbs.querySelector('[data-target="' + next.dataset.mediaId + '"] button');
        if (!button) return false;
        button.click();
        return true;
      }

      // The photos either side of the one showing start downloading (they are display:none and loading="lazy").
      warm() {
        this.warmed = true;
        if (window.atWarmGallery) window.atWarmGallery(this.list);
        [-1, 1].forEach((dir) => {
          const li = this.neighbour(dir);
          const img = li && li.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
        });
      }

      // After any change of photo: the counter, the arrows' end states, the next photos.
      updateCounter() {
        const button = this.thumbs.querySelector('button[aria-current="true"]');
        const thumb = button && button.closest('.at-thumb');
        if (thumb && this.counter) {
          this.counter.textContent = Array.prototype.indexOf.call(this.thumbs.children, thumb) + 1 + ' / ' + this.thumbs.children.length;
        }
        this.arrows.forEach((arrow) => arrow.setAttribute('aria-disabled', String(!this.neighbour(Number(arrow.dataset.atStep)))));
        if (this.warmed) this.warm();
      }

      onArrow(event) {
        this.warmed = true;
        this.step(Number(event.currentTarget.dataset.atStep));
      }

      onDown(event) {
        this.swiped = false;
        this.start =
          this.heroShowing() && (event.pointerType !== 'mouse' || this.mq.matches)
            ? { x: event.clientX, y: event.clientY, id: event.pointerId }
            : null;
      }

      onUp(event) {
        if (!this.start || event.pointerId !== this.start.id) return;
        const dx = event.clientX - this.start.x;
        const dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        this.swiped = true;
        this.step(dx < 0 ? 1 : -1);
      }

      onClickCapture(event) {
        if (!this.swiped) return;
        this.swiped = false;
        event.preventDefault();
        event.stopPropagation();
      }

      onKey(event) {
        if (!this.heroShowing() || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
        if (!this.step(event.key === 'ArrowRight' ? 1 : -1)) return;
        event.preventDefault();
        // The old slide is display:none now; keep focus on the photograph that is showing.
        const toggle = this.list.querySelector('li.is-active .product__media-toggle');
        if (toggle) toggle.focus({ preventScroll: true });
      }
    }
  );
}

/* Leaving an open product accordion for sizing help: save the open rows and the link's position on this history
   entry. Back can rebuild the document instead of using bfcache; restoring only scrollY then lands in the reviews
   because the chart is closed. Restore the rows first, then the link, after the browser's own Back restoration. */
if (!window.atProductReturnBound) {
  window.atProductReturnBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.at-buy .at-acc a[href]');
    if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname === location.pathname) return;
    const accordion = link.closest('details');
    history.replaceState({ ...history.state, atProductReturn: {
      open: Array.from(document.querySelectorAll('.at-buy details[open]'), (row) => row.id),
      row: accordion.id,
      link: Array.from(accordion.querySelectorAll('a[href]')).indexOf(link),
      top: link.getBoundingClientRect().top
    } }, '');
  });
  let scrollMode;
  const restoreRows = (saved) => {
    // WebKit can restore its old scroll after pageshow. This entry has our own anchor; let it finish once.
    if (scrollMode === undefined) {
      scrollMode = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }
    const buy = document.querySelector('.at-buy');
    if (buy) buy.classList.add('is-restoring');
    document.querySelectorAll('.at-buy details').forEach((row) => { row.open = saved.open.includes(row.id); });
  };
  const nav = performance.getEntriesByType('navigation')[0];
  // Expand before native scroll restoration and anchoring see the rebuilt page's height.
  if (nav && nav.type === 'back_forward' && history.state && history.state.atProductReturn) restoreRows(history.state.atProductReturn);
  window.addEventListener('pagehide', () => {
    if (scrollMode === undefined) return;
    history.scrollRestoration = scrollMode;
    scrollMode = undefined;
  });
  window.addEventListener('pageshow', (event) => {
    const saved = history.state && history.state.atProductReturn;
    if (!saved || (!event.persisted && (!nav || nav.type !== 'back_forward'))) return;
    restoreRows(saved);
    const row = document.getElementById(saved.row);
    const link = row && row.querySelectorAll('a[href]')[saved.link];
    if (link) link.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (link) {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--at-header-h')) || 0;
        window.scrollBy({ top: link.getBoundingClientRect().top - Math.max(saved.top, header + 16), behavior: 'instant' });
      }
      const buy = document.querySelector('.at-buy');
      if (buy) buy.classList.remove('is-restoring');
      const state = { ...history.state };
      delete state.atProductReturn;
      history.replaceState(state, '');
    });
  });
}

/* Reviews (sections/at-reviews.liquid): the app lists ten reviews a page; CSS shows three first. "Read all" (either
   copy, .at-reviews-more__btn) reveals the rest and the app's paging. Both copies hide once used, so focus moves to the
   first newly shown review (not back to <body>) without scrolling. Delegated, so HelpfulCrowd's late render and a
   theme-editor re-render need no re-binding; the flag stops a re-inserted copy of this file binding twice. */
if (!window.atReviewsMoreBound) {
  window.atReviewsMoreBound = true;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('.at-reviews-more__btn');
    const root = btn && btn.closest('.at-reviews');
    if (!root) return;
    const widget = root.querySelector('.at-reviews-widget');
    if (widget) widget.classList.add('is-expanded');
    root.querySelectorAll('.at-reviews-more').forEach((more) => {
      more.firstElementChild.setAttribute('aria-expanded', 'true');
      more.hidden = true;
    });
    const next = root.querySelectorAll('.hc-card__item')[3];
    if (next) {
      next.tabIndex = -1;
      next.focus({ preventScroll: true });
    }
  });
}

/* Reviews paging from the keyboard: HelpfulCrowd redraws the list for the chosen page and the page link that had focus
   is gone, so focus fell to <body> and the next Tab went back up to the photo strip. After a keyboard activation of a
   paging link (a click with detail 0: Enter), the first review of the new page takes focus (tabindex -1) as soon as
   the app has drawn it, scrolling only if it is out of view. One MutationObserver, only between that press and the
   redraw. Mouse and touch paging are untouched. */
if (!window.atReviewsPagingBound) {
  window.atReviewsPagingBound = true;
  let watch = null;
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.at-reviews .hc-paging a');
    if (!link || event.detail !== 0) return;
    const widget = link.closest('.at-reviews-widget');
    if (!widget) return;
    const before = widget.querySelector('.hc-card__item');
    if (watch) watch.disconnect();
    watch = new MutationObserver(() => {
      const first = widget.querySelector('.hc-card__item');
      if (!first || first === before) return;
      watch.disconnect();
      watch = null;
      first.tabIndex = -1;
      const rect = first.getBoundingClientRect();
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--at-header-h')) || 0;
      const inView = rect.top >= header && rect.top < window.innerHeight - 40;
      first.focus({ preventScroll: inView });
    });
    watch.observe(widget, { childList: true, subtree: true });
  }, true); // capture: HelpfulCrowd's own handler stops the click before it would bubble to the document
}

/* Size chart rows that pick their size (markChart marks them data-at-pick and puts a button in the size cell): a tap
   or click anywhere on the row, or Enter / Space on its button (a native click), asks the page's <at-buybox> to choose
   that size. Delegated, because Dawn moves the size guide dialog to <body>; the flag stops a re-inserted copy binding
   twice. */
if (!window.atChartPickBound) {
  window.atChartPickBound = true;
  document.addEventListener('click', (event) => {
    const row = event.target.closest && event.target.closest('.at-size-chart__table tr[data-at-pick]');
    const box = row && document.querySelector('at-buybox');
    if (box && typeof box.pickFromChart === 'function') box.pickFromChart(row.dataset.atPick);
  });
}

/* Size guide popup (sections/main-product.liquid, popup block): the generic page body (the size-guide page) is not
   in the product HTML. When any opener of a popup is pressed, each [data-at-page-body] slot in it (holding a plain
   link to the page, the no-JS fallback) is filled with the page's body, fetched through the Section Rendering API
   (<page url>?section_id=main-page, the .at-rte block, else any .rte). pointerdown starts the request a little before the click
   opens the dialog; keyboard presses arrive as clicks. One retry, then the link stays and the next open tries again.
   Delegated, so a theme-editor re-render needs no re-binding; the flag stops a re-inserted copy binding twice. */
if (!window.atPageBodyBound) {
  window.atPageBodyBound = true;
  /* The page body is the store's own text, so no paragraph can be edited to wrap well. Chrome's text-wrap:pretty
     (at-product.css) leaves some last words alone on a line where Safari does not ("embellishments." at 320-340px),
     so the last two words of each paragraph and list item are joined with a no-break space: the last line always
     holds at least two words, in every browser, at every width. */
  const keepLastWords = (root) => {
    root.querySelectorAll('p, li').forEach((el) => {
      const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let last = null;
      for (let n = walk.nextNode(); n; n = walk.nextNode()) if (n.nodeValue.trim()) last = n;
      if (last) last.nodeValue = last.nodeValue.replace(/ (\S{1,16}\s*)$/, '\u00a0$1');
    });
  };
  const fill = (slot, tries) => {
    const url = slot.dataset.atPageBody;
    return fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=main-page`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('.at-rte') || doc.querySelector('.rte');
        if (!body) throw new Error('no page body');
        slot.innerHTML = body.innerHTML;
        keepLastWords(slot);
        slot.dataset.state = 'loaded';
      })
      .catch(() => {
        if (tries > 1) return fill(slot, tries - 1);
        delete slot.dataset.state;
      });
  };
  const load = (event) => {
    const opener = event.target.closest && event.target.closest('modal-opener[data-modal]');
    const modal = opener && document.querySelector(opener.dataset.modal);
    if (!modal) return;
    modal.querySelectorAll('[data-at-page-body]:not([data-state])').forEach((slot) => {
      slot.dataset.state = 'loading';
      fill(slot, 2);
    });
  };
  document.addEventListener('pointerdown', load, { passive: true });
  document.addEventListener('click', load);
}

/* Size guide popup and photo lightbox, Tab loop. Dawn's trapFocus notes the dialog's first and last focusable elements
   once, when it opens. In the size guide the last is the size-guide page link inside the closed "Girls Dress Size
   Guide" section (not focusable while closed), which the page body above then replaces; in the lightbox at 800px and
   below it is a thumbnail, hidden there with the arrows. So Tab left the dialog for the page behind it, and Escape
   (heard only inside the dialog) no longer closed it. This reads what can take focus at each Tab: forward from the
   last goes to the first (Close), back from the first (or the dialog) to the last. Capture on document runs before
   Dawn's handler, which is stopped only when this one wraps. A playing video (its controls), a YouTube or Vimeo iframe
   and a 3D model count too, so the loop never skips or strands them. */
if (!window.atPopupTabBound) {
  window.atPopupTabBound = true;
  const focusable = 'summary, a[href], button:not(:disabled), input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled), video[controls], iframe, model-viewer, [tabindex]:not([tabindex^="-"])';
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab') return;
      const content = event.target.closest && event.target.closest('.product-popup-modal[open] .product-popup-modal__content, product-modal[open] > [role=dialog]');
      if (!content) return;
      const items = Array.from(content.querySelectorAll(focusable)).filter(
        (el) => el.getClientRects().length && !el.closest('details:not([open]) > :not(summary)')
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      let to = null;
      if (!event.shiftKey && event.target === last) to = first;
      else if (event.shiftKey && (event.target === first || event.target === content)) to = last;
      if (!to) return;
      event.preventDefault();
      event.stopPropagation();
      to.focus();
    },
    true
  );
}

/* Size guide popup: focus starts on its Close, as in the photo lightbox (product-modal.js). Dawn's show() focuses the
   dialog box itself, so a keyboard user saw the focus ring, plus Dawn's grey glow, drawn round the whole dialog. The
   click reaches the document after modal-opener has opened the popup; nothing moves if focus has gone elsewhere.
   After a mouse click or a tap no ring shows (the X is focused by script, not the keyboard). */
if (!window.atPopupCloseFocusBound) {
  window.atPopupCloseFocusBound = true;
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('modal-opener[data-modal]');
    const modal = opener && document.querySelector(opener.dataset.modal);
    if (!modal || !modal.classList.contains('product-popup-modal') || !modal.hasAttribute('open')) return;
    const box = modal.querySelector('[role=dialog]');
    const close = modal.querySelector('.product-popup-modal__toggle');
    if (close && document.activeElement === box) close.focus({ preventScroll: true });
    const content = modal.querySelector('.product-popup-modal__content-info');
    if (!content) return;
    if (opener.hasAttribute('data-at-measure')) {
      const image = modal.querySelector('.at-size-chart__img');
      const more = modal.querySelector('.at-size-chart__more');
      const target = image || more;
      if (!image && more) more.open = true;
      if (target) {
        const title = modal.querySelector('.at-popup-title');
        content.scrollTop += target.getBoundingClientRect().top - content.getBoundingClientRect().top - (title ? title.offsetHeight : 0) - 16;
      }
    } else {
      content.scrollTop = 0;
    }
  });
}

/* <at-pairs>: "Pairs well with" (main-product block `pairs`), live's Rebuy product widget 308648 without Rebuy's
   script. The block renders a heading and ghost rows of the final row height. Once the page has loaded (or on the
   first scroll, tap or key, if that comes first) it asks Rebuy's public API for its list (data-list; Rebuy leaves out
   this product, sold-out and unpublished items), then fetches each product's row from sections/at-pairs-card.liquid
   (the Section Rendering API on that product's URL, one retry: the dev proxy drops some section requests), so the
   price, currency, photo and stock are Shopify's. Each row takes its ghost's place as soon as it arrives (same
   height, same order, so nothing moves); ghosts beyond Rebuy's count go at once. A row that comes back empty (sold out
   since Rebuy cached it, unpublished), fails twice or has not arrived 8 s after it was asked for loses its ghost; a
   Rebuy answer that has not arrived within 6 s counts as an error. No rows at all, a Rebuy error or no answer hide the
   block. The deadlines are AbortSignal.timeout (browsers without AbortSignal.any simply wait).
   Each row is a Dawn <product-form>: Add posts /cart/add and opens the cart drawer. The Rebuy line-item properties
   (data-properties: _source "Rebuy", _attribution "Rebuy Product", _widget_id) are added to every row's form here, so
   Rebuy credits the sale as it does for its own widget. A row with a select must have a choice first: the submit is
   stopped (capture, before Dawn's handler) and the select is focused and marked invalid.
   Listeners and requests end in disconnectedCallback (theme-editor re-renders). No observers. */
if (!customElements.get('at-pairs')) {
  customElements.define(
    'at-pairs',
    class AtPairs extends HTMLElement {
      connectedCallback() {
        this.ac = new AbortController();
        const signal = this.ac.signal;
        this.start = this.start.bind(this);
        this.addEventListener('submit', (event) => this.onSubmit(event), { capture: true, signal });
        this.addEventListener('change', (event) => this.onChange(event), { signal });
        if (this.done) return;
        if (document.readyState === 'complete') return this.start();
        this.waiting = new AbortController();
        const opts = { once: true, passive: true, signal: this.waiting.signal };
        ['load', 'scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((type) => window.addEventListener(type, this.start, opts));
      }

      disconnectedCallback() {
        if (this.waiting) this.waiting.abort();
        if (this.ac) this.ac.abort();
        this.started = false;
      }

      start() {
        if (this.started || this.done) return;
        this.started = true;
        if (this.waiting) this.waiting.abort();
        const signal = this.ac.signal;
        const d = this.dataset;
        const limit = Math.max(1, parseInt(d.limit, 10) || 3);
        const api =
          `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(d.list)}` +
          `?key=${encodeURIComponent(d.key)}&shopify_product_ids=${encodeURIComponent(d.productId)}&limit=${limit}`;
        fetch(api, { signal: this.deadline(signal, 6000), credentials: 'omit', priority: 'low' })
          .then((response) => {
            if (!response.ok) throw new Error(`Rebuy HTTP ${response.status}`);
            return response.json();
          })
          .then((json) => {
            const seen = new Set([String(d.productId)]);
            const handles = [];
            ((json && json.data) || []).forEach((p) => {
              if (!p || !p.handle || seen.has(String(p.id))) return;
              seen.add(String(p.id));
              handles.push(p.handle);
            });
            const ghosts = Array.from(this.querySelectorAll('.at-pair--ghost'));
            ghosts.slice(handles.length).forEach((ghost) => ghost.remove());
            return Promise.all(
              handles.slice(0, ghosts.length).map((handle, i) =>
                this.row(handle, this.deadline(signal, 8000), 2).then((row) => {
                  if (!signal.aborted) this.place(ghosts[i], row);
                })
              )
            );
          })
          .then(() => this.finish())
          .catch(() => {
            if (!signal.aborted) this.finish();
          });
      }

      // The component's signal, also aborted after ms (where the browser can combine signals).
      deadline(signal, ms) {
        return AbortSignal.any && AbortSignal.timeout ? AbortSignal.any([signal, AbortSignal.timeout(ms)]) : signal;
      }

      row(handle, signal, tries) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return fetch(`${root}products/${encodeURIComponent(handle)}?section_id=at-pairs-card`, { signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('.at-pair'))
          .catch(() => (signal.aborted || tries <= 1 ? null : this.row(handle, signal, tries - 1)));
      }

      // One row in its ghost's place (the Rebuy properties on its form, the block's Add label), or the ghost gone.
      place(ghost, row) {
        if (!row) return ghost.remove();
        let props = {};
        try {
          props = JSON.parse(this.dataset.properties || '{}');
        } catch (e) {}
        const form = row.querySelector('form');
        Object.keys(props).forEach((key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = props[key];
          if (form) form.appendChild(input);
        });
        const label = row.querySelector('.at-pair__add > span');
        if (label) label.textContent = this.dataset.add || 'Add';
        ghost.replaceWith(row);
      }

      // Every row has arrived or been dropped: any ghost left goes, and a block with no rows hides.
      finish() {
        this.done = true;
        this.querySelectorAll('.at-pair--ghost').forEach((ghost) => ghost.remove());
        const list = this.querySelector('.at-pairs__list');
        if (list) list.removeAttribute('aria-busy');
        if (!list || !list.children.length) this.hidden = true;
      }

      onSubmit(event) {
        const select = event.target.querySelector && event.target.querySelector('.at-pair__select');
        if (!select || select.value) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        select.setAttribute('aria-invalid', 'true');
        select.focus();
      }

      onChange(event) {
        if (event.target.matches && event.target.matches('.at-pair__select')) event.target.removeAttribute('aria-invalid');
      }
    }
  );
}

/* Hosted gallery videos use the photograph underneath until a decoded frame plays. Desktop columns follow
   visibility; phone strips follow the nearest slide, and hero layouts keep Dawn's is-active selection. Explicit
   pause survives scrolling away and back. The lightbox retains its separate player and native controls. */
if (!customElements.get('at-product-video')) {
  customElements.define('at-product-video', class AtProductVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      this.slide = this.closest('li[data-media-id]');
      this.list = this.closest('.at-gallery');
      if (!this.video || !this.list) return;
      this.video.muted = true;
      this.poster = this.parentElement.querySelector('.product__media img');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.phone = matchMedia('(max-width: 800px)');
      this.connection = navigator.connection;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      };
      this.poster?.addEventListener('load', this.schedule, { signal });
      this.button.addEventListener('click', () => {
        this.userPaused = !this.video.paused || this.pending;
        this.manual = !this.userPaused;
        if (this.userPaused) this.video.pause();
        else { this.failed = false; this.attach(); this.play(); }
      }, { signal });
      this.video.addEventListener('playing', () => {
        this.button.setAttribute('aria-pressed', 'true');
        const reveal = () => { if (!this.video.paused) this.video.classList.add('has-frame'); };
        if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
        else requestAnimationFrame(reveal);
      }, { signal });
      this.video.addEventListener('pause', () => this.button.setAttribute('aria-pressed', 'false'), { signal });
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.video.classList.remove('has-frame');
        this.button.setAttribute('aria-pressed', 'false');
      }, { signal });
      this.nearObserver = new IntersectionObserver((entries) => {
        this.near = entries[0].isIntersecting;
        this.schedule();
      }, { rootMargin: innerHeight + 'px 0px' });
      this.viewObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].intersectionRatio >= 0.5;
        this.schedule();
      }, { threshold: [0, 0.5] });
      this.nearObserver.observe(this);
      this.viewObserver.observe(this);
      this.changes = new MutationObserver(this.schedule);
      this.changes.observe(this.list, { attributes: true, childList: true, subtree: true, attributeFilter: ['class'] });
      this.changes.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['inert'] });
      ['load', 'resize', 'pageshow'].forEach((type) => addEventListener(type, this.schedule, { signal }));
      addEventListener('pagehide', () => this.video.pause(), { signal });
      this.list.addEventListener('scroll', this.schedule, { passive: true, signal });
      document.addEventListener('visibilitychange', this.schedule, { signal });
      this.motion.addEventListener('change', this.schedule, { signal });
      this.connection?.addEventListener('change', this.schedule, { signal });
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.nearObserver?.disconnect();
      this.viewObserver?.disconnect();
      this.changes?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.video?.pause();
    }

    autoplayAllowed() {
      return !this.motion.matches && !(this.connection && (this.connection.saveData || /(^|-)2g$/.test(this.connection.effectiveType || '')));
    }

    focused() {
      if (!this.inView) return false;
      const hero = this.list.classList.contains('is-hero') || (this.phone.matches && this.list.classList.contains('is-hero-mobile'));
      if (hero) return this.slide.classList.contains('is-active');
      if (!this.phone.matches) return true;
      const box = this.list.getBoundingClientRect();
      const centre = box.left + box.width / 2;
      let nearest = null, distance = Infinity;
      Array.from(this.list.children).forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const next = Math.abs(rect.left + rect.width / 2 - centre);
        if (rect.width && next < distance) { distance = next; nearest = slide; }
      });
      return nearest === this.slide;
    }

    attach() {
      if (this.video.hasAttribute('src')) return;
      const sources = JSON.parse(this.video.dataset.video).sources.filter((s) => s.format === 'mp4' && s.height <= 720).sort((a, b) => a.width - b.width);
      const width = this.clientWidth * devicePixelRatio;
      const best = sources.find((s) => s.width >= width) || sources[sources.length - 1];
      if (best) this.video.src = best.url;
    }

    play() {
      if (!this.video.hasAttribute('src') || !this.video.paused || this.pending || this.failed) return;
      this.pending = true;
      this.video.play().then(() => { this.pending = false; }, (error) => {
        this.pending = false;
        this.failed = error.name !== 'AbortError';
        this.button.setAttribute('aria-pressed', 'false');
      });
    }

    update() {
      const allowed = this.manual || this.autoplayAllowed();
      const ready = document.readyState === 'complete' || this.manual;
      if (ready && allowed && this.near) this.attach();
      const painted = this.poster?.complete && this.poster.naturalWidth;
      if (painted && ready && allowed && !this.userPaused && !document.hidden && !this.closest('[inert]') && this.focused()) this.play();
      else this.video.pause();
    }
  });
}
